/**
 * CMS QA Chat - Theme Defaults & Utilities
 *
 * CSS変数名とデフォルト値のマッピング。
 * 外部サイトは init({ theme: { ... } }) で上書き可能。
 */

// テーマキー(camelCase) → CSS変数名 のマッピング
const THEME_VAR_MAP = {
  // ヘッダー
  headerBg: '--cms-chat-header-bg',
  headerColor: '--cms-chat-header-color',
  headerFontSize: '--cms-chat-header-font-size',

  headerPadding: '--cms-chat-header-padding',

  // コンテナ
  bg: '--cms-chat-bg',
  borderRadius: '--cms-chat-border-radius',
  fontFamily: '--cms-chat-font-family',
  fontSize: '--cms-chat-font-size',
  lineHeight: '--cms-chat-line-height',
  shadow: '--cms-chat-shadow',
  containerMaxWidth: '--cms-chat-max-width',

  // ユーザーメッセージ
  userMsgBg: '--cms-chat-user-msg-bg',
  userMsgColor: '--cms-chat-user-msg-color',

  // アシスタントメッセージ
  assistantMsgBg: '--cms-chat-assistant-msg-bg',
  assistantMsgColor: '--cms-chat-assistant-msg-color',

  // システムメッセージ
  systemMsgBg: '--cms-chat-system-msg-bg',
  systemMsgColor: '--cms-chat-system-msg-color',
  systemMsgBorder: '--cms-chat-system-msg-border',

  // メッセージ共通
  msgMaxWidth: '--cms-chat-msg-max-width',
  msgBorderRadius: '--cms-chat-msg-border-radius',
  msgPadding: '--cms-chat-msg-padding',
  msgFontSize: '--cms-chat-msg-font-size',

  // メッセージ一覧エリア
  messagesAreaPadding: '--cms-chat-messages-padding',
  messagesAreaGap: '--cms-chat-messages-gap',

  // 入力エリア
  inputBorderColor: '--cms-chat-input-border-color',
  inputBorderRadius: '--cms-chat-input-border-radius',
  inputBg: '--cms-chat-input-bg',
  inputColor: '--cms-chat-input-color',
  inputPadding: '--cms-chat-input-padding',
  inputMinHeight: '--cms-chat-input-min-height',
  inputFontSize: '--cms-chat-input-font-size',

  // 送信ボタン
  sendBtnBg: '--cms-chat-send-btn-bg',
  sendBtnColor: '--cms-chat-send-btn-color',
  sendBtnHoverBg: '--cms-chat-send-btn-hover-bg',
  sendBtnDisabledBg: '--cms-chat-send-btn-disabled-bg',
  sendBtnPadding: '--cms-chat-send-btn-padding',
  sendBtnFontSize: '--cms-chat-send-btn-font-size',
  sendBtnBorderRadius: '--cms-chat-send-btn-border-radius',

  // ステータスバー
  statusColor: '--cms-chat-status-color',
  statusBorderColor: '--cms-chat-status-border-color',
  statusFontSize: '--cms-chat-status-font-size',
  statusPadding: '--cms-chat-status-padding',

  // リンク
  linkColor: '--cms-chat-link-color',

  // タイピングインジケーター
  typingBg: '--cms-chat-typing-bg',
  typingDotColor: '--cms-chat-typing-dot-color',
};

// デフォルト値
const THEME_DEFAULTS = {
  headerBg: '#0078d4',
  headerColor: '#ffffff',
  headerFontSize: '1.5rem',
  headerPadding: '15px 20px',

  bg: '#ffffff',
  borderRadius: '8px',
  fontFamily: "'Hiragino Sans', 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif",
  fontSize: '1rem',
  lineHeight: '1.6',
  shadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  containerMaxWidth: '800px',

  userMsgBg: '#dcf8c6',
  userMsgColor: '#333333',

  assistantMsgBg: '#f0f0f0',
  assistantMsgColor: '#333333',

  systemMsgBg: '#f8f8f8',
  systemMsgColor: '#666666',
  systemMsgBorder: '1px solid #eeeeee',

  msgMaxWidth: '80%',
  msgBorderRadius: '18px',
  msgPadding: '10px 15px',
  msgFontSize: '1rem',

  messagesAreaPadding: '20px',
  messagesAreaGap: '15px',

  inputBorderColor: '#dddddd',
  inputBorderRadius: '20px',
  inputBg: '#ffffff',
  inputColor: '#333333',
  inputPadding: '10px 15px',
  inputMinHeight: 'auto',
  inputFontSize: '1rem',

  sendBtnBg: '#0078d4',
  sendBtnColor: '#ffffff',
  sendBtnHoverBg: '#006cbe',
  sendBtnDisabledBg: '#cccccc',
  sendBtnPadding: '10px 20px',
  sendBtnFontSize: '1rem',
  sendBtnBorderRadius: '20px',

  statusColor: '#666666',
  statusBorderColor: '#eeeeee',
  statusFontSize: '0.8rem',
  statusPadding: '10px',

  linkColor: '#0078d4',

  typingBg: '#f0f0f0',
  typingDotColor: '#999999',
};

/**
 * テーマのCSS変数をコンテナ要素に適用する
 * @param {HTMLElement} container - チャットコンテナ要素
 * @param {Object} userTheme - ユーザー指定のテーマ値 (camelCaseキー)
 */
export function applyTheme(container, userTheme = {}) {
  if (!container) return;

  const merged = { ...THEME_DEFAULTS, ...userTheme };

  Object.keys(merged).forEach((key) => {
    const cssVar = THEME_VAR_MAP[key];
    if (cssVar) {
      container.style.setProperty(cssVar, merged[key]);
    }
  });
}

export { THEME_VAR_MAP, THEME_DEFAULTS };
